import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  IconButton,
  MenuItem,
  Button,
  Chip,
  Tooltip,
} from "@mui/material";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import ChatIcon from "@mui/icons-material/Chat";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";

// Mock data for documents with revisions
// const documentData = [
//   {
//     document: "Procurement of Tank Plates",
//     docNum: "001",
//     revNum: "3",
//     docLink: "https://example.com/project-plan",
//     progress: 85,
//     responsible: "Omar Mokhtar",
//     revisions: [
//       { version: "Rev 1", date: "2023-01-01", changes: "Initial draft" },
//       { version: "Rev 2", date: "2023-02-15", changes: "Added milestones" },
//       { version: "Rev 3", date: "2023-03-10", changes: "Finalized content" },
//     ],
//   },
//   {
//     document: "Procurement of Nozzles",
//     docNum: "002",
//     revNum: "2",
//     docLink: "https://example.com/budget-overview",
//     progress: 90,
//     responsible: "Omar Mokhtar",
//     revisions: [
//       { version: "Rev 1", date: "2023-01-20", changes: "Initial draft" },
//       {
//         version: "Rev 2",
//         date: "2023-02-10",
//         changes: "Updated budget figures",
//       },
//     ],
//   },
// ];

const RACITable = ({
  data,
  handleButtonClick,
  allUsers,
  currentUser,
  openNewRaciModal,
  updateRaciData,
  setSelectedDocuments,
  onArchiveRaci,
}) => {
  const racis = data || [];
  const [rowSelection, setRowSelection] = useState({});

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "docNum",
        header: "Document #",
        size: 160,
      },
      {
        accessorKey: "revNum",
        header: "Rev",
        size: 50,
        enableEditing: (row) =>
          currentUser
            ? row.original?.task_members?.includes(currentUser)
            : false,
      },
      {
        accessorKey: "document",
        header: "Description",
        size: 140,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "docLink",
        header: "link",
        size: 60,
        Cell: ({ cell }) => {
          const href = cell.getValue();
          if (!href) return "—";
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              Link
            </a>
          );
        },
      },
      {
        accessorKey: "docTransmittalNumber",
        header: "Transmittal #",
        size: 140,
        accessorFn: (row) =>
          row.docTransmittalNumber ??
          row.docTransmittal?.number ??
          row.transmittalNumber ??
          "",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "—";
          return (
            <Box
              component="span"
              sx={{ color: "success.main", fontWeight: 700 }}
            >
              {value}
            </Box>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 90,
        Cell: ({ row }) =>
          row.original?.status ??
          row.original?.lifecycle ??
          row.original?.purpose ??
          "—",
      },
      // {
      //   accessorKey: "Approved",
      //   header: "Approved",
      //   enableColumnActions: false,
      //   enableSorting: false,
      //   size: 70, // Reduced width
      //   muiEditTextFieldProps: () => ({
      //     children: allUsers?.map((user) => (
      //       <MenuItem key={user.email} value={user.email}>
      //         {user.email}
      //       </MenuItem>
      //     )),
      //     select: true,
      //   }),
      // },
      {
        accessorKey: "responsible",
        header: "Responsible (R)",
        enableColumnActions: false,
        enableSorting: false,
        size: 70, // Reduced width
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        // Extract email from responsible object for display and editing
        accessorFn: (row) => {
          if (typeof row.responsible === "object" && row.responsible?.email) {
            return row.responsible.email;
          }
          return row.responsible || "";
        },
        Cell: ({ cell, row }) => {
          const responsible = row.original.responsible;
          const label =
            typeof responsible === "object" && responsible
              ? responsible.name || responsible.email
              : cell.getValue();

          if (!label) return "-";

          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Chip
                size="small"
                label={label}
                // keep as display-only for now; editing is still via inline edit
                variant="outlined"
              />
            </Box>
          );
        },
        muiEditTextFieldProps: () => ({
          children: allUsers?.map((user) => (
            <MenuItem key={user.email} value={user.email}>
              {user.email}
            </MenuItem>
          )),
          select: true,
        }),
      },
      {
        accessorKey: "accountable",
        header: "Accountable (A)",
        enableEditing: false,
        size: 100, // Reduced width
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const raw = row.original.accountable || [];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

          if (!items.length) {
            return (
              <Box
                sx={{ minHeight: 32, display: "flex", alignItems: "center" }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleButtonClick("acc", row)}
                >
                  Add
                </Button>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name || item?.email || "";
                if (!label) return null;
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={label}
                    onClick={() => handleButtonClick("acc", row)}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "consulted",
        header: "Consulted (C)",
        size: 100, // Reduced width
        enableEditing: false,
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const raw = row.original.consulted || [];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

          if (!items.length) {
            return (
              <Box
                sx={{ minHeight: 32, display: "flex", alignItems: "center" }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleButtonClick("cons", row)}
                >
                  Add
                </Button>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name || item?.email || "";
                if (!label) return null;
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={label}
                    onClick={() => handleButtonClick("cons", row)}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "informed",
        header: "Informed (I)",
        enableEditing: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 60, // Reduced width
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => {
          const raw = row.original.informed || [];
          const items = Array.isArray(raw) ? raw : raw ? [raw] : [];

          if (!items.length) {
            return (
              <Box
                sx={{ minHeight: 32, display: "flex", alignItems: "center" }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={() => handleButtonClick("inf", row)}
                >
                  Add
                </Button>
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                minHeight: 32,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {items.map((item, idx) => {
                const label =
                  typeof item === "string"
                    ? item
                    : item?.name || item?.email || "";
                if (!label) return null;
                return (
                  <Chip
                    key={idx}
                    size="small"
                    label={label}
                    onClick={() => handleButtonClick("inf", row)}
                    variant="outlined"
                  />
                );
              })}
            </Box>
          );
        },
      },
      {
        accessorKey: "mail",
        header: "Mail",
        enableColumnActions: false,
        enableSorting: false,
        enableEditing: false,
        size: 60, // Reduced width
        Cell: ({ row }) => (
          <IconButton onClick={() => handleButtonClick("mail", row)}>
            <AttachEmailIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "chat",
        header: "Chat",
        enableColumnActions: false,
        enableSorting: false,
        enableEditing: false,
        size: 60, // Reduced width
        Cell: ({ row }) => (
          <IconButton onClick={() => handleButtonClick("comments", row)}>
            <ChatIcon />
          </IconButton>
        ),
      },
      {
        accessorKey: "progress",
        header: "Progress",
        size: 70,
        enableEditing: false,
        Cell: ({ row }) => {
          const timesheets = row.original?.timesheets || [];
          const progress = timesheets.reduce(
            (acc, t) => acc + (t?.progress || 0),
            0,
          );
          return `${progress}%`;
        },
      },
      // {
      //   accessorKey: "timesheet",
      //   header: "Timesheet",
      //   enableEditing: false,
      //   enableColumnActions: false,
      //   enableSorting: false,
      //   size: 70,
      //   muiTableHeadCellProps: { align: "center" },
      //   muiTableBodyCellProps: { align: "center" },
      //   Cell: ({ row }) => (
      //     <IconButton
      //       onClick={() => handleButtonClick("timesheet", row)}
      //       aria-label="open timesheet"
      //     >
      //       <PostAddIcon />
      //     </IconButton>
      //   ),
      // },
      {
        accessorKey: "archive",
        header: "Archive",
        enableEditing: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 70,
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => (
          <Tooltip title="Archive">
            <IconButton
              onClick={() => onArchiveRaci?.(row.original)}
              aria-label="archive"
            >
              <ArchiveOutlinedIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ];
  }, [currentUser, allUsers, handleButtonClick, updateRaciData, onArchiveRaci]);

  // Sync selectedDocuments with rowSelection when it changes
  React.useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );
    const selectedDocs = racis.filter((raci) =>
      selectedIds.includes(String(raci.id))
    );
    setSelectedDocuments(selectedDocs);
  }, [rowSelection, racis, setSelectedDocuments]);

  const table = useMaterialReactTable({
    columns,
    data: racis || [],
    // enableColumnOrdering: true,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
    paginationDisplayMode: "pages",
    enableEditing: true,
    onEditingRowSave: async ({ values, table, row }) => {
      const raciId = row.original.id;
      // Handle responsible field - if it's an email string, keep it; if it's an object, extract email
      const responsibleValue =
        typeof values.responsible === "string"
          ? values.responsible
          : values.responsible?.email || row.original.responsible?.email || "";

      const updateData = {
        ...values,
        responsible: responsibleValue,
      };

      if (updateRaciData && raciId) {
        await updateRaciData(raciId, updateData);
      }
      table.setEditingRow(null);
    },
    renderTopToolbarCustomActions: () => {
      return (
        <Button onClick={openNewRaciModal} variant="contained">
          Add Document
        </Button>
      );
    },
  });

  return <MaterialReactTable table={table} />;
};

export default RACITable;
